import type { FormOption } from '../../components/forms/forms.types';
import type { IntranetMenuBody, IntranetPointBody } from '../../api/types/intranet.types';
import { IntranetTexts } from './IntranetTexts';

/** What is read from the flat list of points of an intranet. */
class IntranetTreeServiceClass {
    /**
     * The address of an intranet or of a point in it: `/intranet/2/dwc/22/blub`. Only the ids count, the tokens are there for
     * reading; `Intranet.svelte` corrects an address with other ones.
     */
    public getHref(intranet: IntranetMenuBody, point: Pick<IntranetPointBody, 'intranet_point_id' | 'title'> | null): string {
        const Base = `/intranet/${intranet.intranet_id.toString()}/${this.toToken(intranet.intranet)}`;

        return point === null ? Base : `${Base}/${point.intranet_point_id.toString()}/${this.toToken(point.title)}`;
    }

    public listChildren(points: IntranetPointBody[], parentId: number | null): IntranetPointBody[] {
        return points.filter((point: IntranetPointBody): boolean => point.parent_id === parentId);
    }

    /** The point and everything below it. */
    public listBranchIds(points: IntranetPointBody[], pointId: number): number[] {
        return [pointId, ...this.listChildren(points, pointId).flatMap((child: IntranetPointBody): number[] => this.listBranchIds(points, child.intranet_point_id))];
    }

    private readonly TokenChars: Readonly<Record<string, string>> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
    private readonly TokenLength = 25;

    /** The value of the root in a choice of points. */
    public readonly RootValue = 0;

    /** The choices of the tree in its order, a point reads „Eins | Zwei | Drei“, with `withCount` „… (4)“. */
    public listOptions(points: IntranetPointBody[], withCount: boolean): FormOption[] {
        return this.listBranchOptions(points, null, [], withCount);
    }

    /** A text as a short part of an address: lower case, ASCII, words joined by `-`. */
    private toToken(text: string): string {
        const Ascii = text
            .toLowerCase()
            .replace(/[äöüß]/g, (char: string): string => this.TokenChars[char])
            .normalize('NFKD')
            .replace(/\p{M}/gu, '');
        const Token = Ascii.replace(/[^a-z0-9]+/g, '-')
            .slice(0, this.TokenLength)
            .replace(/^-+|-+$/g, '');

        return Token === '' ? IntranetTexts.TokenFallback : Token;
    }

    private listBranchOptions(points: IntranetPointBody[], parentId: number | null, path: string[], withCount: boolean): FormOption[] {
        return this.listChildren(points, parentId).flatMap((point: IntranetPointBody): FormOption[] => {
            const Path = [...path, point.title];
            const Label = Path.join(IntranetTexts.PathSeparator);

            return [
                { value: point.intranet_point_id, label: withCount ? IntranetTexts.format(IntranetTexts.PointLabel, { path: Label, count: point.entry_count }) : Label },
                ...this.listBranchOptions(points, point.intranet_point_id, Path, withCount),
            ];
        });
    }
}

export const IntranetTreeService = new IntranetTreeServiceClass();
